import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productsAPI } from '../../services/api';
import { Product, ProductStatus } from '../../types';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CubeIcon,
  TagIcon,
  BuildingOfficeIcon,
  PhotoIcon,
  ScaleIcon,
  ShoppingBagIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (id) {
      loadProduct();
    }
  }, [id]);

  const loadProduct = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const productData = await productsAPI.getById(id);
      setProduct(productData);
    } catch (err) {
      setError('Failed to load product');
      console.error('Error loading product:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!product) return;

    try {
      setDeleting(true);
      await productsAPI.delete(product.id);
      navigate('/products');
    } catch (err) {
      setError('Failed to delete product');
      console.error('Error deleting product:', err);
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const getStatusIcon = (status: ProductStatus) => {
    switch (status) {
      case ProductStatus.ACTIVE:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case ProductStatus.INACTIVE:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case ProductStatus.DISCONTINUED:
        return <ArchiveBoxIcon className="h-5 w-5 text-gray-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: ProductStatus) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case ProductStatus.ACTIVE:
        return `${baseClasses} bg-green-100 text-green-800`;
      case ProductStatus.INACTIVE:
        return `${baseClasses} bg-red-100 text-red-800`;
      case ProductStatus.DISCONTINUED:
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInventoryStatus = (inventory: number, minInventory: number) => {
    if (inventory === 0) {
      return { status: 'out-of-stock', color: 'text-red-600', bgColor: 'bg-red-50' };
    } else if (inventory <= minInventory) {
      return { status: 'low-stock', color: 'text-orange-600', bgColor: 'bg-orange-50' };
    } else {
      return { status: 'in-stock', color: 'text-green-600', bgColor: 'bg-green-50' };
    }
  };

  const getTagsArray = (tags: any): string[] => {
    if (!tags) return [];
    if (Array.isArray(tags)) return tags;
    if (typeof tags === 'string') {
      try {
        // Try to parse as JSON if it's a string
        const parsed = JSON.parse(tags);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        // If it's not JSON, treat as a single tag
        return tags.trim() ? [tags] : [];
      }
    }
    return [];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">Product not found</h3>
        <p className="mt-1 text-sm text-gray-500">The product you're looking for doesn't exist.</p>
      </div>
    );
  }

  const inventoryStatus = getInventoryStatus(product.inventory, product.minInventory);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/products"
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{product.name}</h1>
            <p className="text-gray-600">Product Details</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Link
            to={`/products/${product.id}/edit`}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </Link>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Product Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CubeIcon className="h-5 w-5 mr-2" />
              Basic Information
            </h3>
            
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Product Name</span>
                <div className="text-sm text-gray-900">{product.name}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Status</span>
                <div className="flex items-center mt-1">
                  {getStatusIcon(product.status)}
                  <span className={`ml-2 ${getStatusBadge(product.status)}`}>
                    {product.status}
                  </span>
                </div>
              </div>
              <div className="lg:col-span-2">
                <span className="text-sm font-medium text-gray-700">Description</span>
                <div className="text-sm text-gray-900 mt-1">
                  {product.description || 'No description provided'}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">SKU</span>
                <div className="text-sm text-gray-900">{product.sku || 'Not specified'}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Barcode</span>
                <div className="text-sm text-gray-900">{product.barcode || 'Not specified'}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Category</span>
                <div className="text-sm text-gray-900">{product.category || 'Not specified'}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Brand</span>
                <div className="text-sm text-gray-900">{product.brand || 'Not specified'}</div>
              </div>
            </div>
          </div>

          {/* Pricing Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 mr-2" />
              Pricing Information
            </h3>
            
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Price</span>
                <div className="text-lg font-semibold text-gray-900">{formatCurrency(product.price)}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Cost</span>
                <div className="text-sm text-gray-900">{formatCurrency(product.cost)}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Profit Margin</span>
                <div className="text-sm text-gray-900">
                  {product.cost > 0 
                    ? `${(((product.price - product.cost) / product.price) * 100).toFixed(1)}%`
                    : 'N/A'
                  }
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Currency</span>
                <div className="text-sm text-gray-900">{product.currency?.code || 'USD'}</div>
              </div>
            </div>
          </div>

          {/* Inventory Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <ShoppingBagIcon className="h-5 w-5 mr-2" />
              Inventory Information
            </h3>
            
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Current Inventory</span>
                <div className={`text-lg font-semibold ${inventoryStatus.color}`}>
                  {product.inventory} units
                </div>
                <div className={`text-xs mt-1 px-2 py-1 rounded-full inline-block ${inventoryStatus.bgColor} ${inventoryStatus.color}`}>
                  {inventoryStatus.status === 'out-of-stock' && 'Out of Stock'}
                  {inventoryStatus.status === 'low-stock' && 'Low Stock'}
                  {inventoryStatus.status === 'in-stock' && 'In Stock'}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Minimum Inventory</span>
                <div className="text-sm text-gray-900">{product.minInventory} units</div>
              </div>
            </div>
          </div>

          {/* Physical Properties */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <ScaleIcon className="h-5 w-5 mr-2" />
              Physical Properties
            </h3>
            
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Weight</span>
                <div className="text-sm text-gray-900">
                  {product.weight > 0 ? `${product.weight} kg` : 'Not specified'}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Dimensions</span>
                <div className="text-sm text-gray-900">{product.dimensions || 'Not specified'}</div>
              </div>
            </div>
          </div>

          {/* Tags */}
          {(() => {
            const tagsArray = getTagsArray(product.tags);
            return tagsArray.length > 0 ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <TagIcon className="h-5 w-5 mr-2" />
                  Tags
                </h3>
                
                <div className="flex flex-wrap gap-2">
                  {tagsArray.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ) : null;
          })()}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Company Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 mr-2" />
              Company Information
            </h3>
            
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-700">Company</span>
                <div className="text-sm text-gray-900">{product.company?.name}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Location</span>
                <div className="text-sm text-gray-900">
                  {product.company?.city && product.company?.state 
                    ? `${product.company.city}, ${product.company.state}`
                    : 'Not specified'
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Timestamps</h3>
            
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-700">Created</span>
                <div className="text-sm text-gray-900">{formatDate(product.createdAt)}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Last Updated</span>
                <div className="text-sm text-gray-900">{formatDate(product.updatedAt)}</div>
              </div>
            </div>
          </div>

          {/* Image Preview */}
          {product.image && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <PhotoIcon className="h-5 w-5 mr-2" />
                Product Image
              </h3>
              
              <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                <img
                  src={product.image}
                  alt={product.name}
                  className="max-w-full max-h-full object-contain rounded-lg"
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden items-center justify-center text-gray-400">
                  <PhotoIcon className="h-12 w-12" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4 text-center">Delete Product</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 text-center">
                  Are you sure you want to delete "{product.name}"? This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
